import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { TrendingUp, TrendingDown, Info } from "lucide-react";

export const FirstTrade = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Placing Your First Trade</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Step-by-step guide to opening your first perpetual position on Nordex</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-6">
            Trading on Nordex is straightforward. You'll use the Order Form panel to enter your trade parameters, 
            select your leverage, and open either a long (buy) or short (sell) position. Let's walk through the process.
          </p>
        </section>

        {/* Understanding Long and Short */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Long vs Short Positions</h2>
          
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Long Position</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                You profit when the price goes <strong className="text-foreground">up</strong>.
              </p>
              <p className="text-sm text-muted-foreground">
                Example: Open a long BTC position at $50,000. If BTC rises to $55,000, you profit. 
                If it falls to $45,000, you lose.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <TrendingDown className="h-6 w-6 text-red-500" />
                </div>
                <h3 className="font-semibold text-lg">Short Position</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                You profit when the price goes <strong className="text-foreground">down</strong>.
              </p>
              <p className="text-sm text-muted-foreground">
                Example: Open a short ETH position at $3,000. If ETH falls to $2,700, you profit. 
                If it rises to $3,300, you lose.
              </p>
            </div>
          </div>
        </section>

        {/* Step by Step */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Step-by-Step Trading Guide</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Select Your Market</h3>
                <p className="text-sm text-muted-foreground">
                  Use the market selector in the header to choose which cryptocurrency you want to trade. 
                  Browse all available markets from the dropdown.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Locate the Order Form Panel</h3>
                <p className="text-sm text-muted-foreground">
                  The Order Form is typically on the left side of the screen on desktop, or in the "Trade" 
                  tab on mobile. This is where you'll enter your trade parameters.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Enter Collateral Amount</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  In the "Collateral" input field, enter the amount of USDC you want to use as collateral 
                  for this position. This is the amount at risk, not the total position size.
                </p>
                <div className="p-3 bg-muted/30 rounded-lg text-sm">
                  <strong>Example:</strong> If you enter 100 USDC as collateral with 10x leverage, 
                  your total position size will be 1,000 USDC worth of the asset.
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Select Your Leverage</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Use the leverage slider to choose your desired leverage level (1x to 100x). 
                  Higher leverage amplifies both potential profits and losses.
                </p>
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
                  <strong>⚠️ Warning:</strong> Higher leverage means higher risk. Start with lower leverage 
                  (1x-5x) until you're comfortable with how it works.
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <h3 className="font-semibold mb-1">Review Position Details</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Before placing your order, review the calculated details:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                  <li><strong className="text-foreground">Position Size:</strong> Total value of your position (Collateral × Leverage)</li>
                  <li><strong className="text-foreground">Entry Price:</strong> Current market price where your position will open</li>
                  <li><strong className="text-foreground">Liquidation Price:</strong> Price at which your position will be automatically closed</li>
                  <li><strong className="text-foreground">Trading Fee:</strong> Fee charged for opening the position (see <a href="/fees" className="text-primary hover:underline">Fee Structure</a>)</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  You can also enable "Take Profit / Stop Loss" in the order form to set automatic exit prices 
                  for your position. See <a href="/order-types" className="text-primary hover:underline">Order Types</a> for details.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                6
              </div>
              <div>
                <h3 className="font-semibold mb-1">Place Your Order</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Click either the <strong className="text-emerald-500">Long</strong> button (if you think 
                  the price will go up) or the <strong className="text-red-500">Short</strong> button 
                  (if you think the price will go down).
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                7
              </div>
              <div>
                <h3 className="font-semibold mb-1">Confirm the Transaction</h3>
                <p className="text-sm text-muted-foreground">
                  If you're using a wallet connection, you'll be prompted to approve the transaction. 
                  Review the details and confirm.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                8
              </div>
              <div>
                <h3 className="font-semibold mb-1">Position Opened!</h3>
                <p className="text-sm text-muted-foreground">
                  Your position will appear in the Positions panel. You can now monitor your profit/loss 
                  in real-time as the market price changes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Monitoring Your Position */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Monitoring Your Position</h2>
          
          <p className="text-muted-foreground mb-4">
            Once your position is open, you can track it in the Positions panel. You'll see:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li><strong className="text-foreground">Market:</strong> Which asset you're trading</li>
            <li><strong className="text-foreground">Side:</strong> Long or Short</li>
            <li><strong className="text-foreground">Size:</strong> Total position value in USD</li>
            <li><strong className="text-foreground">Entry Price:</strong> Price where you opened the position</li>
            <li><strong className="text-foreground">Current Price:</strong> Live market price</li>
            <li><strong className="text-foreground">Unrealized P&L:</strong> Your current profit or loss (updates in real-time)</li>
            <li><strong className="text-foreground">Liquidation Price:</strong> Price at which your position will be liquidated</li>
          </ul>

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <strong className="text-foreground">Unrealized P&L</strong> means the profit or loss 
                hasn't been "realized" yet because the position is still open. It becomes realized P&L 
                when you close the position.
              </div>
            </div>
          </div>
        </section>

        {/* Closing Your Position */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Closing Your Position</h2>
          
          <p className="text-muted-foreground mb-4">
            When you're ready to close your position and lock in your profit or loss:
          </p>

          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Find your open position in the Positions panel
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Click the "✕ Close" button on the right side of the position row
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Confirm the closure. Your position will be closed at the current market price, 
                  and your realized P&L will be added to (or subtracted from) your account balance
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Important Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Important Trading Tips</h2>
          
          <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
            <li>
              <strong className="text-foreground">Start small</strong> -- for your first trades, use small amounts of collateral and low leverage (1x-3x) 
              to get comfortable with the platform.
            </li>
            <li>
              <strong className="text-foreground">Watch your liquidation price</strong> -- always check your liquidation price before opening a position. If the market price 
              reaches this level, your position will be automatically closed to prevent further losses.
            </li>
            <li>
              <strong className="text-foreground">Understand funding rates</strong> -- perpetual positions incur funding payments every 8 hours. Depending on market conditions, 
              you may pay or receive funding. Check the Funding Rate panel for current rates.
            </li>
            <li>
              <strong className="text-foreground">Configure slippage tolerance</strong> -- click the gear icon in the header to set your slippage tolerance for market orders. 
              Presets include Auto, 0.1%, 0.25%, 0.5%, and 1%.
            </li>
            <li>
              <strong className="text-foreground">Monitor your margin ratio</strong> -- keep an eye on your margin ratio in the Balances panel. If it drops below 150%, 
              take action to avoid liquidation. Consider adding more collateral or reducing your positions.
            </li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Deposits & Withdrawals", href: "/withdrawals" }}
          next={{ title: "Understanding the Interface", href: "/interface" }}
        />
      </div>
    </DocsLayout>
  );
};

